import React from 'react';
import AuthenticatedInput from '../AuthenticatedInput';
import { updateExamField } from '../../utils/examData';
import {
  calculateWristFlexion, calculateWristExtension,
  calculateWristRadialDeviation, calculateWristUlnarDeviation,
} from '../TabRatingNarratives';
import { useExamTab } from './ExamTabContext';

const WristsTab: React.FC = () => {
  const {
    examData, setExamData, user, userProfile, startManualSession, setShowAuthModal,
    copyRegionValues, handleClearRegion, clearConfirmRegion, fillNormalValues,
  } = useExamTab();

  return (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Wrists</h3>

                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">

                  {/* Wrist Rating Section — live WPI instrument (right column) */}
                  {examData?.wrists && (
                    <div className="lg:order-2 lg:sticky lg:top-20">
                       <div id="wrists-rating" className="card p-4 flex flex-col gap-4">
                        <div>
                          <span className="font-medium">Right Wrist WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                          {(() => {
                            const data = examData?.wrists;
                            if (!data) return "--";
                            
                            // Check if right wrist has ROM values
                            if (data.right_flexion === null && 
                                data.right_extension === null && 
                                data.right_radial_deviation === null && 
                                data.right_ulnar_deviation === null) {
                              return "--";
                            }
                            
                            // Calculate individual impairments
                            const rightFlexion = calculateWristFlexion(data.right_flexion ?? null);
                            const rightExtension = calculateWristExtension(data.right_extension ?? null);
                            const rightRadialDeviation = calculateWristRadialDeviation(data.right_radial_deviation ?? null);
                            const rightUlnarDeviation = calculateWristUlnarDeviation(data.right_ulnar_deviation ?? null);
                            
                            // Calculate joint sum and convert to WPI
                            const rightSum = rightFlexion + rightExtension + rightRadialDeviation + rightUlnarDeviation;
                            const rightWPI = Math.round(rightSum * 0.6);
                            
                            return `${rightWPI}%`;
                          })()}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Left Wrist WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                          {(() => {
                            const data = examData?.wrists;
                            if (!data) return "--";
                            
                            // Check if left wrist has ROM values
                            if (data.left_flexion === null && 
                                data.left_extension === null && 
                                data.left_radial_deviation === null && 
                                data.left_ulnar_deviation === null) {
                              return "--";
                            }
                            
                            // Calculate individual impairments
                            const leftFlexion = calculateWristFlexion(data.left_flexion ?? null);
                            const leftExtension = calculateWristExtension(data.left_extension ?? null);
                            const leftRadialDeviation = calculateWristRadialDeviation(data.left_radial_deviation ?? null);
                            const leftUlnarDeviation = calculateWristUlnarDeviation(data.left_ulnar_deviation ?? null);
                            
                            // Calculate joint sum and convert to WPI
                            const leftSum = leftFlexion + leftExtension + leftRadialDeviation + leftUlnarDeviation;
                            const leftWPI = Math.round(leftSum * 0.6);
                            
                            return `${leftWPI}%`;
                          })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Range of Motion Section — left column */}
                  <div className="lg:order-1 mb-0">
                    <div className="flex justify-between items-center mb-4 border-b border-border pb-2">
                      <h4 className="text-lg font-medium">Range of Motion</h4>
                      <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('wrists', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('wrists', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('wrists')} className={clearConfirmRegion === 'wrists' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'wrists' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('wrists')} className="btn-secondary text-xs" disabled={!user}>Fill Normal Values</button></div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                      {/* Header Row */}
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                      <div className="col-span-1 text-center font-medium">Right</div>
                      <div className="col-span-1 text-center font-medium">Left</div>

                      {/* Flexion Row */}
                      <div className="col-span-1 text-content-secondary">Flexion</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">60°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.wrists.right_flexion ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'wrists', 'right_flexion', value));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.wrists.left_flexion ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'wrists', 'left_flexion', value));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>

                      {/* Extension Row */}
                      <div className="col-span-1 text-content-secondary">Extension</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">60°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.wrists.right_extension ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'wrists', 'right_extension', value));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.wrists.left_extension ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'wrists', 'left_extension', value));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>

                      {/* Radial Deviation Row */}
                      <div className="col-span-1 text-content-secondary">Radial Deviation</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">20°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.wrists.right_radial_deviation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'wrists', 'right_radial_deviation', value));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.wrists.left_radial_deviation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'wrists', 'left_radial_deviation', value));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>

                      {/* Ulnar Deviation Row */}
                      <div className="col-span-1 text-content-secondary">Ulnar Deviation</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">30°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.wrists.right_ulnar_deviation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'wrists', 'right_ulnar_deviation', value));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.wrists.left_ulnar_deviation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'wrists', 'left_ulnar_deviation', value));
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                    </div>
                  </div>
                  </div>

                  {/* Wrist Rating Section has been moved above the Range of Motion section */}
                </div>
  );
};

export default WristsTab;
